import { useLoad } from "@redotech/react-util/load";
import { AutomationForgeElementType } from "@redotech/redo-content-builder-common/forge/marketing-automation-forge-types";
import { TrackingPageStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-03.svg";
import { RedoOrderTrackingRpcClientContext } from "@redotech/redo-merchant-app-common/order-tracking-rpc-client-provider";
import { useOptionalAutomationMetricsDateRange } from "@redotech/redo-merchant-marketing-app/automations/automation-metrics-date-range-context";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { isDef } from "@redotech/util/checks";
import { Handle, Position } from "@xyflow/react";
import { memo, useContext } from "react";
import { FlowEdge, NodeTypes, TypedFlowNode } from "../flow-converter";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as css from "./node.module.css";
import { TrackingPageAnalyticsBlock } from "./tracking-page-analytics-block";

export interface TrackingPageNodeData {
  step: PartialWithRequiredType<TrackingPageStep>;
  deleteElements: (id: string) => void;
  readonly: boolean;
}

export const TrackingPageNode = memo(function TrackingPageNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: TrackingPageNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const orderTrackingRpcClient = useContext(RedoOrderTrackingRpcClientContext);
  const trackingPageId = data.step.trackingPageId;

  const dateRangeCtx = useOptionalAutomationMetricsDateRange();
  const rangeStart = dateRangeCtx?.dateRange[0];
  const rangeEnd = dateRangeCtx?.dateRange[1];

  // Per-page analytics over the selected date range.
  const metricsLoad = useLoad(async () => {
    if (!orderTrackingRpcClient || !isDef(trackingPageId)) {
      return undefined;
    }
    let numUnits = 30;
    let startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    if (rangeStart && rangeEnd) {
      numUnits = Math.max(
        rangeStart.until(rangeEnd, { largestUnit: "day" }).days,
        0,
      );
      startDate = new Date(rangeEnd.year, rangeEnd.month - 1, rangeEnd.day);
    }
    const result = await orderTrackingRpcClient.getTrackingPagesMetrics({
      numUnits,
      startDate,
    });
    return result.pages.find((p) => p.trackingPageId === trackingPageId);
  }, [orderTrackingRpcClient, trackingPageId, rangeStart, rangeEnd]);

  const metrics = metricsLoad.value;

  return (
    <>
      <Handle id="top" position={Position.Top} type="target" />
      <BaseNode
        containerClassName={css.checkoutNode}
        icon={
          <RedoFeaturedIcon color="success" Icon={ShoppingCartIcon} size="sm" />
        }
        onDelete={data.readonly ? undefined : () => data.deleteElements(id)}
        selected={selected}
        step={data.step}
        title={t("Tracking page")}
      />
      <TrackingPageAnalyticsBlock
        clicks={metrics?.clicks}
        opens={metrics?.opens}
        revenue={metrics?.revenue}
      />
      <Handle id="bottom" position={Position.Bottom} type="source" />
    </>
  );
});

export function getTrackingPageEdgesAndDropZones({
  stepId,
  nextId,
  elementDropped,
  readonly,
}: {
  stepId: string;
  nextId: string | undefined;
  elementDropped: (
    elementType: AutomationForgeElementType,
    idFrom: string,
    idTo?: string,
    conditionSide?: boolean,
  ) => void;
  readonly: boolean;
}): { edges: FlowEdge[]; dropZoneNodes: TypedFlowNode[] } {
  const edges: FlowEdge[] = [];
  const dropZoneNodes: TypedFlowNode[] = [];

  if (isDef(nextId)) {
    edges.push({
      id: stepId + "_next",
      type: "edge",
      source: stepId,
      sourceHandle: "bottom",
      target: nextId,
      data: {
        idFrom: stepId,
        idTo: nextId,
        ...(!readonly ? { elementDropped } : {}),
      },
    });
  } else if (!readonly) {
    const dropZoneId = stepId + "_next";
    dropZoneNodes.push({
      id: dropZoneId,
      type: NodeTypes.DROP_ZONE,
      position: { x: 0, y: 0 },
      data: { idFrom: stepId, idTo: undefined, elementDropped },
    });
    edges.push({
      id: stepId + "_next_edge",
      type: "dropEdge",
      source: stepId,
      sourceHandle: "bottom",
      target: dropZoneId,
      data: {},
    });
  }

  return { edges, dropZoneNodes };
}
